import { updateTooltips } from '@solar-assistant/shared-ui/js/elements/tooltips.js'

let Hooks = {}

Hooks.HelpMenu = {
    mounted() {
        window.addEventListener("phx:page-loading-stop", event => this.populatePageSelector());
        this.populatePageSelector();
    },
    destroyed() {
        // remove global listener when navigating away
        window.removeEventListener("phx:page-loading-stop", this._onPageStop)
    },
    populatePageSelector(event) {
        const pageIndex = window.document.getElementById("page-index");
        const pageSelector = document.getElementById("page-selector");
        if (!pageIndex || !pageSelector) return;

        while (pageSelector.firstChild) {
            pageSelector.removeChild(pageSelector.firstChild);
        }
        pageIndex.querySelectorAll('.section').forEach(section => {
            const group = document.createElement("optgroup");
            group.setAttribute("label", section.textContent);

            section.nextElementSibling.querySelectorAll('.item').forEach(item => {
                const option = document.createElement("option");
                option.value = item.getAttribute("href");
                if (window.top.location.pathname === item.getAttribute("href")) {
                    option.selected = true
                }
                option.appendChild(document.createTextNode(item.textContent))
                group.appendChild(option);
            })

            pageSelector.appendChild(group);
        });
    }
}


Hooks.HelpPage = {
    dbName() {
        return "sa-cloud"
    },
    storeName() {
        return "help"
    },
    handleKey() {
        return "local-content"
    },
    expandAnchor() {
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        const el = document.getElementById(hash);
        if (!el) return;
        const details = el.tagName === "DETAILS" ? el : el.closest("details");
        if (details) details.open = true;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    async mounted() {
        this.expandAnchor();
        window.addEventListener("phx:page-loading-stop", () => this.expandAnchor());
        window.addEventListener("hashchange", () => this.expandAnchor());

        const editButton = this.el.querySelector("#edit-mode");
        if (!editButton) return;

        this.handleEvent("local-meta-loaded", async (e) => {
            let dirHandle = await this.getStoredHandle();
            await this.loadEditableContent(dirHandle);
        });

        editButton.addEventListener("click", async () => {
            await this.toggleEdit();
        })

        // Attempt auto-load on mount
        await this.tryAutoLoad();
    },

    async toggleEdit() {
        const editButton = this.el.querySelector("#edit-mode");
        let dirHandle = await this.getStoredHandle();

        if (dirHandle) {
            const perm = await dirHandle.queryPermission({mode: "read"});
            if (perm !== "granted") {
                dirHandle = null;
            }
        }

        if (dirHandle) {
            await this.clearDirHandle();
            editButton.classList.remove("active");
            window.location.reload();
        } else {
            try {
                dirHandle = await window.showDirectoryPicker();
                const granted = await dirHandle.requestPermission({mode: "read"});
                if (granted !== "granted") {
                    alert("Permission denied.");
                    return;
                }
                await this.storeHandle(dirHandle);
            } catch (err) {
                console.error("Error selecting folder:", err);
                return;
            }
            await this.loadMetaData(dirHandle);
        }
    },

    parsePaths(multiple, single) {
        let paths = [];
        if (multiple) {
            try {
                paths = JSON.parse(multiple);
            } catch (e) {
                console.error("Invalid JSON in paths:", multiple);
            }
        }

        // Fallback to single path
        if (paths.length === 0 && single) {
            paths = [single];
        }
        return paths;
    },

    async loadMetaData(dirHandle) {
        let paths = this.parsePaths(this.el.dataset.metaPaths, this.el.dataset.metaPath);
        let localMetaFound = false;

        for (const path of paths) {
            const relativePath = path.replace(/^\//, "");
            try {
                const file = await this.readContentFile(dirHandle, relativePath);
                const meta = await file.text();
                localMetaFound = true;
                this.pushEvent("load-local-meta", {content: meta, path: path});
                break;
            } catch (err) {
            }
        }
        if (!localMetaFound) {
            await this.loadEditableContent(dirHandle);
        }
    },

    async loadEditableContent(dirHandle) {
        const editables = this.el.querySelectorAll(".editable");

        for (const el of editables) {
            const paths = this.parsePaths(el.dataset.contentPaths, el.dataset.contentPath);
            let resolved = null;

            for (const path of paths) {
                const relativePath = path.replace(/^\//, "");
                try {
                    const file = await this.readContentFile(dirHandle, relativePath);
                    el.innerHTML = await file.text()
                    resolved = relativePath;
                    await this.expandIncludes(dirHandle, el);
                    this.loadLocalImages(dirHandle, el);
                    await this.expandProducts(el);
                    break;
                } catch (err) {
                }
            }

            this.markLocal(el, dirHandle, paths, resolved);
        }

        this.el.querySelector("#edit-mode").classList.add("active");
        updateTooltips();
        
        // Reinitialize Phoenix hooks for dynamically loaded content
        editables.forEach(el => {
            el.querySelectorAll('[phx-hook]').forEach(hookEl => {
                window.liveSocket.main.maybeAddNewHook(hookEl);
            });
        });
    },

    async tryAutoLoad() {
        const editButton = this.el.querySelector("#edit-mode");
        let dirHandle = await this.getStoredHandle();

        if (dirHandle) {
            const perm = await dirHandle.queryPermission({mode: "read"});
            if (perm === "granted") {
                await this.loadMetaData(dirHandle);
            }
        }
    },

    async openDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName(), 1);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(this.storeName());
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error("IndexedDB failed to open:", request.error);
                reject(request.error);
            };
        });
    },

    async storeHandle(handle) {
        const db = await this.openDB();
        const tx = db.transaction(this.storeName(), "readwrite");
        tx.objectStore(this.storeName()).put(handle, this.handleKey());
        await tx.complete?.();
        db.close();
    },

    async getStoredHandle() {
        const db = await this.openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName(), "readonly");
            const store = tx.objectStore(this.storeName());
            const request = store.get(this.handleKey());
            request.onsuccess = () => {
                db.close();
                resolve(request.result);
            };
            request.onerror = () => {
                console.error("Error retrieving handle:", request.error);
                db.close();
                reject(request.error);
            };
        });
    },

    async clearDirHandle() {
        const db = await this.openDB();
        const tx = db.transaction(this.storeName(), "readwrite");
        tx.objectStore(this.storeName()).delete(this.handleKey());
        await tx.complete?.();
        db.close();
    },

    async readFileFromDir(dirHandle, relativePath) {
        const parts = relativePath.split("/");
        let current = dirHandle;
        for (let i = 0; i < parts.length - 1; i++) {
            current = await current.getDirectoryHandle(parts[i]);
        }
        const fileHandle = await current.getFileHandle(parts.at(-1));
        return await fileHandle.getFile();
    },

    // Insert ".en" before the final extension (foo/bar.html -> foo/bar.en.html).
    enVariant(relativePath) {
        const dot = relativePath.lastIndexOf(".");
        if (dot === -1) return null;
        return relativePath.slice(0, dot) + ".en" + relativePath.slice(dot);
    },

    // Reads a content/meta file, mirroring the server's english_file/2
    // resolution: prefer the plain `<path>.<ext>`, then fall back to the
    // legacy `<path>.en.<ext>` form that most sa_help pages still use.
    async readContentFile(dirHandle, relativePath) {
        try {
            return await this.readFileFromDir(dirHandle, relativePath);
        } catch (err) {
            const enPath = this.enVariant(relativePath);
            if (!enPath) throw err;
            return await this.readFileFromDir(dirHandle, enPath);
        }
    },

    // Mirror the server's expand_include/2: replace each <sa-include src="…"/>
    // with the referenced fragment, wrapped in its own .editable region so it
    // gets the same dashed outline and "search order" bar as a page-level block.
    // The browser parser ignores the self-closing slash on this non-void element
    // and nests following siblings inside the node, so the wrapper is inserted
    // before the node and the node is then unwrapped (its children spliced back
    // in place) rather than assigning outerHTML, which would delete those
    // siblings. Non-recursive: the static NodeList is not re-scanned.
    //
    // src is relative to the sa_help root (e.g. "shop/software/…"), but dirHandle
    // is the section folder the author picked, and content paths are advertised
    // without that section prefix (render_product: logical_path = "shop/" <> path).
    // Strip the leading "<section>/" so the src resolves the same way; a
    // cross-section include has no local handle to resolve against and blanks.
    async expandIncludes(dirHandle, element) {
        const prefix = dirHandle.name + "/";
        for (const node of element.querySelectorAll("sa-include")) {
            let src = node.getAttribute("src");
            const wrapper = document.createElement("div");
            let paths = [];
            let resolved = null;
            if (src) {
                if (src.startsWith(prefix)) src = src.slice(prefix.length);
                const relativePath = src + ".html";
                paths = [relativePath];
                try {
                    const file = await this.readContentFile(dirHandle, relativePath);
                    wrapper.innerHTML = await file.text();
                    resolved = relativePath;
                    this.loadLocalImages(dirHandle, wrapper);
                } catch (err) {
                }
            }
            this.markLocal(wrapper, dirHandle, paths, resolved);
            node.before(wrapper);
            node.replaceWith(...node.childNodes);
        }
    },

    // Mirror the server's expand_product/1 with the card it renders. Runs after
    // loadLocalImages, which would rewrite the card's already-resolved image src.
    // Unwrapped like <sa-include>, since the browser nests following siblings
    // inside a self-closed <sa-product/>.
    async expandProducts(element) {
        for (const node of element.querySelectorAll("sa-product")) {
            const card = document.createElement("div");
            const params = new URLSearchParams();
            const title = node.getAttribute("title");
            if (title) params.set("title", title);

            const response = await fetch(`/shop/product_card/${encodeURIComponent(node.getAttribute("sku") || "")}?${params}`);
            if (response.ok) {
                card.innerHTML = await response.text();
            } else {
                card.textContent = `⚠ <sa-product sku="${node.getAttribute("sku")}">: ${response.status}`;
            }

            node.before(...card.childNodes);
            node.replaceWith(...node.childNodes);
        }
    },

    // Add the dashed-outline .editable treatment and the hover "search order"
    // tooltip bar to a locally-sourced block — .override (bar shown inline) when
    // a file resolved, otherwise the bar only appears on hover.
    markLocal(el, dirHandle, searchPaths, resolvedPath) {
        const tooltip = document.createElement("div");
        tooltip.id = `bar-${Math.random().toString(36).slice(6)}`;
        tooltip.innerHTML = `<h4>\u{1F4C1} ${dirHandle.name} search order:</h4>${searchPaths.join("<br/>")}`;
        tooltip.style.display = 'none';

        const bar = document.createElement("div");
        bar.classList.add("editable-top-bar");
        bar.setAttribute("data-html-tooltip", tooltip.id);
        bar.textContent = resolvedPath ? `\u{1F4C1} ${resolvedPath}` : `\u{1F4C1} Default`;

        el.classList.add("editable", "local");
        if (resolvedPath) el.classList.add("override");
        el.prepend(bar);
        bar.prepend(tooltip);
    },

    loadLocalImages(dirHandle, element) {
        element.querySelectorAll('img').forEach(async img => {
            const src = img.getAttribute('src');
            if (!src) return;

            if (src.startsWith('/docs/')) {
              try {
                  const relativePath = src.split('/docs/').pop();
                  console.log(relativePath)
                  const file = await this.readFileFromDir(dirHandle, relativePath);
                  const base64 = await this.blobToBase64(file);

                  const ext = relativePath.split('.').pop().toLowerCase();
                  const mime = {
                      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
                      svg: 'image/svg+xml', webp: 'image/webp', ico: 'image/x-icon', avif: 'image/avif'
                  }[ext] || 'application/octet-stream';

                  img.setAttribute('src', `data:${mime};base64, ${base64}`);

              } catch (err) {
                  console.warn(`Failed to load local image: ${src}`);
                  console.warn(err);
              }
            } else if (src.startsWith('/')) {
              img.setAttribute('src', '/images' + src);
            }
        });
    },

    async blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
};

export {Hooks}